<template>
  <div class="projet-part">
    <h2 id="charte">{{ $t("projetPage.infoAdministratives.charte.titre") }}</h2>
    <b-message
      v-if="validationActivated && projetAnnuel.charteFilePresent && !projetAnnuel.charteFileValid"
      type="is-warning"
    >
      {{ $t("projetPage.infoAdministratives.charte.unvalid") }}

      <!-- Hidden input to trigger a validation issue in correct section -->
      <div v-show="false">
        <InputWithValidation vid="charte_unvalid" rules="required" />
      </div>
    </b-message>
    <div style="display: flex">
      <BookmarkCard
        :tag-type="tagType"
        icon-name="file-signature"
        class="without-footer grey-header"
      >
        <template #content>
          <div>
            {{ $t("projetPage.infoAdministratives.charte.link") }}
            <a href="https://ame.ofb.fr/doku.php?id=les_chartes" target="_blank" rel="noopener">{{
              $t("projetPage.infoAdministratives.charte.linkTitle")
            }}</a>
            {{ $t("projetPage.infoAdministratives.charte.description") }}
          </div>
        </template>
      </BookmarkCard>
      <BookmarkCard
        :tag-type="tagType"
        icon-name="check-circle"
        icon-type="is-success"
        class="without-footer"
      >
        <template #content>
          <ValidationObserver ref="charteObserver">
            <FileWithValidation
              :validation-activated="validationActivated"
              :show-validation-errors="showValidationErrors"
              :rules="projetAnnuel.typeDossier === 'DOSSIER_RENOUVELLEMENT_LIGHT' ? '' : 'required'"
              :input-notice="$t(`projetPage.infoAdministratives.charte.signee`)"
              :placeholder="$t(`projetPage.infoAdministratives.charte.televerser`)"
              :file-operation-url="charteFileUrl"
              :model-value="projetAnnuel.charteFilePresent"
              vid="charte_ProjetAnnuel"
              :disabled="readonly"
              default-file-name="CHARTE_ENGAGEMENT.pdf"
              @add-file="addCharte"
              @delete-file="deleteCharte"
              @file-uploaded-or-deleted="charteUploadedOrDeleted"
            />
          </ValidationObserver>
        </template>
      </BookmarkCard>
    </div>
  </div>
</template>

<script lang="ts">
import { ProjetAnnuelDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { defineComponent } from "vue"
import { LoginService } from "@/services/rest/login/LoginService"
import { TAG_TYPES_FOR_BOOKMARK_CARD } from "@/model/bean/EnumHelper"
import BookmarkCard from "@/views/generic_components/BookmarkCard.vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import FileWithValidation from "@/views/inputs/FileWithValidation.vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { FileService } from "@/services/rest/utils/FileService"
import { BMessage } from "buefy"

export default defineComponent({
  name: "ProjetCharteView",
  components: {
    BookmarkCard,
    ValidationObserver,
    FileWithValidation,
    InputWithValidation,
    BMessage,
  },
  props: {
    projetAnnuel: {
      type: Object as () => ProjetAnnuelDTO,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["projet-annuel-updated", "trigger-validation", "dirtify-project"],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      fileService: FileService.INSTANCE,
      loggedUser: new UtilisateurDTO(),
      invitations: [] as string[],
      tagType: TAG_TYPES_FOR_BOOKMARK_CARD.CHARTE_ENGAGEMENT,
      charteFileUrl: "",
    }
  },
  watch: {
    projetAnnuel(): void {
      this.dossierChanged()
    },
  },
  created() {
    const loggedUser = this.loginService.getLoggedUser()
    if (!loggedUser || !loggedUser.id) {
      return
    }
    this.loggedUser = loggedUser
    if (this.projetAnnuel.id) {
      this.charteFileUrl = this.fileService.buildUniqueFileURL(
        this.projetAnnuel.id,
        "CHARTE_ENGAGEMENT",
      )
    }
  },
  methods: {
    dossierChanged(): void {
      if (!this.projetAnnuel || !this.projetAnnuel.id) {
        return
      }
      this.charteFileUrl = this.fileService.buildUniqueFileURL(
        this.projetAnnuel.id,
        "CHARTE_ENGAGEMENT",
      )
    },

    addCharte(): void {
      const updatedProjetAnnuel = {
        ...this.projetAnnuel,
        charteFilePresent: true,
        charteFileValid: true,
      }
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      this.$emit("trigger-validation")
    },

    deleteCharte(): void {
      const updatedProjetAnnuel = {
        ...this.projetAnnuel,
        charteFilePresent: false,
        charteFileValid: false,
      }
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      this.$emit("trigger-validation")
    },

    charteUploadedOrDeleted(projetAnnuel: ProjetAnnuelDTO): void {
      const updatedProjetAnnuel = {
        ...this.projetAnnuel,
        fichiers: projetAnnuel.fichiers ? projetAnnuel.fichiers : [],
      }
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      this.$emit("dirtify-project")
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.BookmarkCard {
  &.charte-engagement {
    height: auto;
    margin-left: 10px;
    margin-right: 10px;

    .BookmarkCard-card {
      height: 200px;
      width: 350px;
    }

    .BookmarkCard-icon,
    .BookmarkCard-tag {
      background-color: $ofb-bleu-1;
    }

    .BookmarkCard-icon {
      width: 150px;
    }

    .BookmarkCard-tag {
      width: 150px;
      font-size: 0.8em;
      height: 50px;
      text-align: center;
    }

    .BookmarkCard-content {
      min-height: 150px;
    }

    .BookmarkCard-content div {
      width: 100%;
      padding-left: 15px;
      padding-right: 15px;
      text-align: left;
      font-weight: bold;
    }
  }

  &.grey-header {
    .BookmarkCard-icon,
    .BookmarkCard-tag {
      background-color: $ofb-grey-1;
    }
  }

  &.without-footer {
    .BookmarkCard-footer {
      display: none;
    }
  }
}

div.without-footer:nth-child(2) > div:nth-child(2) > div:nth-child(2) > span:nth-child(1) {
  width: 100%;
}
</style>
